import { Plus, UsersRound } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { Pagination } from '@/components/ui/pagination';
import { SearchInput } from '@/components/ui/search-input';
import { StatusBadge } from '@/components/ui/status-badge';
import { EmployeeFormDialog } from '@/pages/admin/employees/EmployeeFormDialog';
import { useEmployeeFormOptions, useEmployees } from '@/hooks/employees';
import { formatCents } from '@shared/money';
import type { EmployeeAdminView } from '@shared/schemas/employee';
import {
  ACTIVITY_FILTER_OPTIONS,
  DEFAULT_PAGE_SIZE,
  PEOPLE_SORTS,
} from '@shared/schemas/pagination';
import type { ActivityFilter } from '@shared/schemas/pagination';
import { SortSelect } from '@/components/ui/sort-select';
import type { PeopleSort } from '@shared/schemas/pagination';

/*
 * /admin/employees — ROADMAP.md §3.4.
 *
 * Carries the two fields the Airo roadmap dropped and the prototype has:
 * **max jobs a day** and **skills**. Both are visible in the list, because both
 * are what somebody is checking when they open this page to work out who can
 * take a job.
 *
 * The pay-rate column is here because this is the admin portal and nowhere else.
 * The API returns it only from `/api/admin/*`; phase 6's employee portal reads a
 * different shape that does not include the column at all.
 */

export function EmployeesPage() {
  const [search, setSearch] = useState('');
  const [activity, setActivity] = useState<ActivityFilter>('active');
  const [positionId, setPositionId] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<PeopleSort>('name');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<EmployeeAdminView | null>(null);

  const employees = useEmployees({ sort, page, search, activity, positionId, skillId: '' });
  const options = useEmployeeFormOptions();

  const columns: Column<EmployeeAdminView>[] = [
    {
      key: 'name',
      header: 'Name',
      hideLabelOnCard: true,
      render: (row) => (
        <div className="min-w-0">
          {/* The name is the way in to their profile — the same shape the
              clients list uses, so "click the person" works on both. */}
          <Link
            to={`/admin/employees/${row.id}`}
            className="block text-body font-medium text-bark-700 underline underline-offset-2"
          >
            {row.fullName}
          </Link>
          <span className="mt-0.5 block text-meta text-ink-500">
            {/* The login, so "who is sam?" is answerable from the list. A
                username account has no email at all — that is the P9 shape. */}
            {row.email ?? row.username ?? 'No login'}
          </span>
        </div>
      ),
    },
    {
      key: 'position',
      header: 'Position',
      render: (row) =>
        row.positionName ? (
          <span className="text-body">{row.positionName}</span>
        ) : (
          <span className="text-meta text-ink-500">Not set</span>
        ),
    },
    {
      key: 'skills',
      header: 'Skills',
      render: (row) =>
        row.skills.length === 0 ? (
          // "None" rather than a dash. The prototype shows this column with
          // nothing behind it; an empty answer is still an answer (P1).
          <span className="text-meta text-ink-500">None</span>
        ) : (
          <div className="flex flex-wrap justify-end gap-1 md:justify-start">
            {row.skills.map((entry) => (
              <span
                key={entry.id}
                className="rounded-full bg-ink-100 px-2 py-0.5 text-meta text-ink-700"
              >
                {entry.name}
              </span>
            ))}
          </div>
        ),
    },
    {
      key: 'maxJobs',
      header: 'Max jobs/day',
      render: (row) => <span className="text-body">{row.maxJobsPerDay}</span>,
    },
    {
      key: 'rate',
      header: 'Pay rate',
      render: (row) =>
        row.hourlyRateCents === null ? (
          <span className="text-meta text-ink-500">Not recorded</span>
        ) : (
          <span className="whitespace-nowrap text-body">
            {formatCents(row.hourlyRateCents)}
            <span className="text-meta text-ink-500"> / hour</span>
          </span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) =>
        row.isActive ? (
          <span className="text-meta text-ink-500">Active</span>
        ) : (
          <StatusBadge label="Inactive" tone="cancelled" />
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      render: (row) => (
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to={`/admin/employees/${row.id}`}>View profile</Link>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setEditing(row);
            }}
          >
            Edit
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AdminPage
      title="Employees"
      description="The cleaners, what they can do, and how many jobs a day each will take."
      actions={
        <Button
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus aria-hidden="true" />
          Add employee
        </Button>
      }
    >
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <SearchInput
            value={search}
            onChange={(value) => {
              setSearch(value);
              setPage(1);
            }}
            label="Search employees"
            placeholder="Name, phone or login"
            className="sm:w-64"
          />

          <label className="flex items-center gap-2">
            <span className="sr-only">Filter by position</span>
            <select
              name="positionId"
              value={positionId}
              onChange={(event) => {
                setPositionId(event.target.value);
                setPage(1);
              }}
              className="min-h-control rounded-sm border border-ink-300 bg-white px-3 text-body"
            >
              <option value="">All positions</option>
              {(options.data?.positions ?? []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <FilterPills
          label="Filter employees by status"
          options={ACTIVITY_FILTER_OPTIONS}
          value={activity}
          onChange={(next) => {
            setActivity(next);
            setPage(1);
          }}
        />
      </div>

      {/* The order control. A select rather than clickable headers —

          the list becomes cards below 768px and a card has no headers. */}

      <div className="mb-3 flex justify-end">
        <SortSelect
          label="Sort employees"

          options={PEOPLE_SORTS}

          value={sort}

          onChange={(next) => {
            setSort(next);

            // Page 1, or you are on page 3 of a differently ordered list.

            setPage(1);
          }}
        />
      </div>

      <DataTable
        caption="Employees, their position, skills, capacity and pay rate"
        columns={columns}
        rows={employees.data?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={employees.isPending}
        error={employees.error}
        onRetry={() => {
          void employees.refetch();
        }}
        empty={
          <EmptyState
            icon={UsersRound}
            title={search || positionId ? 'No employees match those filters.' : 'No employees yet.'}
            hint={
              search || positionId
                ? 'Try clearing the search or the position filter.'
                : 'Adding someone here creates their login as well, so they can sign in to the employee portal.'
            }
            action={
              search || positionId ? null : (
                <Button
                  onClick={() => {
                    setCreating(true);
                  }}
                >
                  <Plus aria-hidden="true" />
                  Add employee
                </Button>
              )
            }
          />
        }
      />

      {employees.data ? (
        <Pagination
          page={employees.data.page}
          pageSize={employees.data.pageSize || DEFAULT_PAGE_SIZE}
          total={employees.data.total}
          onPageChange={setPage}
          itemNoun="employees"
        />
      ) : null}

      <EmployeeFormDialog open={creating} onOpenChange={setCreating} />
      <EmployeeFormDialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        {...(editing ? { employee: editing } : {})}
      />
    </AdminPage>
  );
}
